import styles from "./contact.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPaperPlane,
  faBusinessTime,
  faCircleCheck,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  faLinkedin,
  faInstagram,
  faFacebook,
  faPinterest,
} from "@fortawesome/free-brands-svg-icons";
import { FormEvent, useEffect, useState } from "react";
import { createEnquiry } from "../../service/enquiry";
import {
  getAllSeries,
  getProductsBySeries,
} from "../../service/galleryService";
import { useAuth } from "../../context/AuthContext";
import LoginModal from "../login";
import { Modal, ModalBody } from "react-bootstrap";

interface EnquiryFormData {
  fullName: string;
  company?: string;
  email: string;
  phone: string;
  quantity: string;
  message: string;
  pincode: string;
  address: string;
  seriesId: string;
  productId: string;
  interest?: string;
}

interface Series {
  _id: string;
  title: string;
}

interface Product {
  _id: string;
  name: string;
}
const Contact = () => {
  const [formData, setFormData] = useState<EnquiryFormData>({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    quantity: "",
    message: "",
    pincode: "",
    address: "",
    seriesId: "",
    productId: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [productList, setProductList] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const { user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Fetch all series on component mount
  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const series = await getAllSeries();
        setSeriesList(series);
      } catch (error) {
        console.error("Failed to fetch series:", error);
      }
    };

    fetchSeries();
  }, []);

  // Fetch products when a series is selected
  useEffect(() => {
    if (!formData.seriesId) {
      setProductList([]);
      setFormData((prev) => ({ ...prev, productId: "" }));
      return;
    }

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const products = await getProductsBySeries(formData.seriesId);

        setProductList(products.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setProductList([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [formData.seriesId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (user === null) {
      return setShowLoginModal(true);
    }
    // Validate series and product selection
    if (!formData.seriesId || !formData.productId) {
      setSubmitStatus({
        success: false,
        message: "Please select both a product series and model",
      });
      return;
    }

    // Find selected series and product
    const selectedSeries = seriesList.find((s) => s._id === formData.seriesId);
    const selectedProduct = productList.find(
      (p) => p._id === formData.productId
    );

    if (!selectedSeries || !selectedProduct) {
      setSubmitStatus({
        success: false,
        message: "Invalid series or product selection. Please try again.",
      });
      return;
    }

    // Create interest string
    const interest = `Product: ${selectedSeries.title} - ${selectedProduct.name}`;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create submission data with dynamically generated interest
      const submissionData = {
        ...formData,
        interest,
      };

      await createEnquiry(submissionData);
      setSubmitStatus({
        success: true,
        message:
          "Your enquiry has been submitted successfully! Our team will contact you within 24 hours.",
      });

      // Reset form
      setFormData({
        fullName: "",
        company: "",
        email: "",
        phone: "",
        quantity: "",
        message: "",
        pincode: "",
        address: "",
        seriesId: "",
        productId: "",
        interest: "",
      });
      setProductList([]);
    } catch (error) {
      setSubmitStatus({
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to submit enquiry. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <section className={styles.contactSection}>
        {/* Hero Banner */}
        <div className={styles.heroBanner}>
          <div className={styles.container}>
            <h1 className={styles.pageTitle}>Contact SCADEL Furnitures</h1>
            <p className={styles.pageDescription}>
              Let's discuss your commercial furniture needs. Our team is ready
              to assist with quotes, custom solutions, and expert advice for
              your space.
            </p>
          </div>
        </div>

        <div className={styles.contactContent}>
          <div className={styles.container}>
            <div className={styles.contactGrid}>
              {/* Contact Information */}
              <div className={styles.contactInfo}>
                <div className={styles.contactCard}>
                  <div className={styles.contactIcon}>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <h3>Email Us</h3>
                  <div className={styles.contactDetails}>
                    <a href="mailto:sales@scadelfurnitures.com">
                      sales@scadelfurnitures.com
                    </a>
                    <a href="mailto:support@scadelfurnitures.com">
                      support@scadelfurnitures.com
                    </a>
                  </div>
                </div>

                {/* <div className={styles.contactCard}>
                <div className={styles.contactIcon}>
                  <FontAwesomeIcon icon={faPhoneAlt} />
                </div>
                <h3>Call Us</h3>
                <div className={styles.contactDetails}>
                  <a href="tel:+915043217890">+91 50 4321 7890</a>
                  <a href="tel:+919876543210">+91 98 7654 3210</a>
                </div>
              </div> */}

                <div className={styles.contactCard}>
                  <div className={styles.contactIcon}>
                    <FontAwesomeIcon icon={faBusinessTime} />
                  </div>
                  <h3>Business Hours</h3>
                  <div className={styles.contactDetails}>
                    <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p>Saturday: 10:00 AM - 4:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>

                {/* <div className={styles.contactCard}>
                <div className={styles.contactIcon}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <h3>Showroom Address</h3>
                <div className={styles.contactDetails}>
                  <p>
                    SCADEL Furnitures Headquarters
                    <br />
                    45, Commercial Complex
                    <br />
                    MG Road, Bengaluru
                    <br />
                    Karnataka 560001
                  </p>
                  <a
                    href="https://maps.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.directionsLink}
                  >
                    Get Directions
                  </a>
                </div>
              </div> */}

                <div className={styles.socialLinks}>
                  <h3>Follow Our Work</h3>
                  <p className={styles.socialDescription}>
                    See our latest collections and projects
                  </p>
                  <div className={styles.socialIcons}>
                    <a
                      href="https://www.linkedin.com/company/scadel-furnitures"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                    >
                      <FontAwesomeIcon icon={faLinkedin} />
                    </a>
                    <a
                      href="https://www.instagram.com/scadelfurnitures"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                    >
                      <FontAwesomeIcon icon={faInstagram} />
                    </a>
                    <a
                      href="https://www.facebook.com/scadelfurnitures"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                    >
                      <FontAwesomeIcon icon={faFacebook} />
                    </a>
                    <a
                      href="https://www.pinterest.com/scadelfurnitures"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Pinterest"
                    >
                      <FontAwesomeIcon icon={faPinterest} />
                    </a>
                  </div>
                </div>
              </div>

              <div className={styles.contactForm}>
                <h2 className={styles.formTitle}>Enquire About Our Products</h2>
                <p className={styles.formSubtitle}>
                  Complete the form below and our design consultant will contact
                  you within 24 hours
                </p>

                {/* Submission Status */}
                {submitStatus && (
                  <div
                    className={`${styles.statusAlert} ${
                      submitStatus.success ? styles.success : styles.error
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={
                        submitStatus.success
                          ? faCircleCheck
                          : faCircleExclamation
                      }
                      className={styles.statusIcon}
                    />
                    {submitStatus.message}
                  </div>
                )}
                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.formGroup}>
                    <div className={styles.formRow}>
                      <div className={styles.formCol}>
                        <label htmlFor="fullName">Full Name*</label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Your name"
                          className={styles.formInput}
                          required
                        />
                      </div>
                      <div className={styles.formCol}>
                        <label htmlFor="company">Company/Organization</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your company name"
                          className={styles.formInput}
                        />
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formCol}>
                        <label htmlFor="email">Email Address*</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className={styles.formInput}
                          required
                        />
                      </div>
                      <div className={styles.formCol}>
                        <label htmlFor="phone">Phone Number*</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 "
                          className={styles.formInput}
                          required
                        />
                      </div>
                    </div>

                    {/* Series Dropdown */}
                    <div className={styles.formRow}>
                      <div className={styles.formColFull}>
                        <label htmlFor="seriesId">Product Series*</label>
                        <select
                          id="seriesId"
                          name="seriesId"
                          value={formData.seriesId}
                          onChange={handleChange}
                          className={styles.formSelect}
                          required
                        >
                          <option value="">Select a product series</option>
                          {seriesList.map((series) => (
                            <option key={series._id} value={series._id}>
                              {series.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Product Dropdown */}
                    <div className={styles.formRow}>
                      <div className={styles.formColFull}>
                        <label htmlFor="productId">Product Model*</label>
                        <select
                          id="productId"
                          name="productId"
                          value={formData.productId}
                          onChange={handleChange}
                          className={styles.formSelect}
                          disabled={!formData.seriesId || loadingProducts}
                          required
                        >
                          <option value="">Select a product model</option>
                          {loadingProducts ? (
                            <option value="">Loading products...</option>
                          ) : (
                            productList.map((product) => (
                              <option key={product._id} value={product._id}>
                                {product.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formCol}>
                        <label htmlFor="quantity">Estimated Quantity</label>
                        <select
                          id="quantity"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          className={styles.formSelect}
                        >
                          <option value="">Select quantity range</option>
                          <option value="1-10">1-10 units</option>
                          <option value="11-50">11-50 units</option>
                          <option value="51-100">51-100 units</option>
                          <option value="100+">100+ units</option>
                          <option value="custom">Custom bulk order</option>
                        </select>
                      </div>
                      <div className={styles.formCol}>
                        <label htmlFor="pincode">PIN Code</label>
                        <input
                          type="text"
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="Enter PIN code"
                          className={styles.formInput}
                        />
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formColFull}>
                        <label htmlFor="address">Address</label>
                        <textarea
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          rows={2}
                          placeholder="Enter your full address"
                          className={styles.formTextarea}
                        ></textarea>
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formColFull}>
                        <label htmlFor="message">Project Details*</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          placeholder="Tell us about your project, space requirements, and any specific needs..."
                          className={styles.formTextarea}
                          required
                        ></textarea>
                      </div>
                    </div>

                    <div className={styles.formFooter}>
                      <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          "Submitting..."
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} /> Submit
                            Enquiry
                          </>
                        )}
                      </button>
                      <p className={styles.privacyNote}>
                        We respect your privacy. Your information will not be
                        shared.
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Google Map */}
          {/* <div className={styles.mapContainer}>
          <h2 className={styles.mapTitle}>Visit Our Showroom</h2>
          <p className={styles.mapDescription}>
            Experience our furniture collections in person at our Bengaluru
            showroom. Schedule a private viewing with our design consultants.
          </p>
          <div className={styles.mapWrapper}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1838200.248198222!2d83.7322464043172!3d25.67585615503029!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x398d2a781b6f5e01%3A0x34392a242512a5f8!2sPatna%2C%20Bihar!5e0!3m2!1sen!2sin!4v1718629920000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SCADEL Furnitures Showroom Location"
            ></iframe>
          </div>
        </div> */}
        </div>
      </section>
      {showLoginModal && (
        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
          <ModalBody style={{ padding: "0px", margin: "0px" }}>
            <LoginModal isStandalone={true} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default Contact;
